import type { ApolloError, FetchResult } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { Obj, Str } from '@livecontrol/core-utils';
import type { Destination } from '@livecontrol/scheduler/model';
import { useCallback, useState } from 'react';
import { Errors } from '../graphql';
import type { MutationResult } from '../graphql';
import * as Dbx from './dbx';
import { QUERY as destinationsQuery } from './use-destinations';

type TVariables = Pick<Destination, 'name' | 'rtmp'> & {
  clientId: string;
};

interface TData {
  createCustomIntegration: Dbx.Record;
}

type Args = TVariables;

const MUTATION = gql`
  mutation CreateCustomIntegration(
    $clientId: String!
    $name: String!
    $rtmpDestination: String!
    $streamKey: String!
  ) {
    createCustomIntegration(
      clientId: $clientId
      integration: { name: $name, rtmp_destination: $rtmpDestination, rtmp_stream_key: $streamKey }
    ) {
      ...StandardDestinationResponse
    }
  }
  ${Dbx.StandardDestinationResponse}
`;

export const useAddRTMP = (): [
  (args: Args) => Promise<Destination | undefined>,
  MutationResult<Destination, 'destination'>
] => {
  const [mutation, result] = useMutation<TData, TVariables>(MUTATION);

  const [error, setError] = useState<Error | undefined>();
  const [destination, setDestination] = useState<Destination | undefined>();

  return [
    useCallback(
      async (args: Args): Promise<Destination | undefined> => {
        let destination_: Destination | undefined;

        try {
          // Parse the input arguments
          const input = {
            clientId: Str.normalize(args.clientId),
            name: Str.normalize(args.name),
            rtmpDestination: Str.normalize(args.rtmp?.destination),
            streamKey: Str.normalize(args.rtmp?.streamKey)
          };

          if (!Obj.isHydrated(input)) {
            throw Errors.badRequest();
          }

          // Execute the GraphQL mutation
          const response = await mutation({
            variables: input,
            refetchQueries: [destinationsQuery]
          })
            .then(
              ({ data }: FetchResult<TData>): Dbx.Record | undefined =>
                data?.createCustomIntegration
            )
            .catch((_error: ApolloError) => {
              throw Errors.serverError();
            });

          // Parse the server response
          if (response === undefined) {
            throw new Error('Empty response received!');
          }

          destination_ = Dbx.normalize(response);

          if (!destination_) {
            throw Errors.serverError();
          }
        } catch (error_: unknown) {
          setError(<Error>error_);
        }

        setDestination(destination_);

        return destination_;
      },
      [mutation, setDestination, setError]
    ),
    {
      destination,
      error,
      called: result.called,
      loading: result.loading
    }
  ];
};
